import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'
import type { ModalidadeEnsino, TurnoReferencia, ValorReferenciaRefeicao } from '../../types/database'
import { TURNOS_REFERENCIA } from '../../types/database'

type ScalarKey =
  | 'energia_kcal'
  | 'energia_kj'
  | 'vitamina_a_mcg'
  | 'vitamina_c_g'
  | 'calcio_mg'
  | 'ferro_mg'

type RangeNutrient = 'carboidratos' | 'proteinas' | 'lipideos'

type RangeKey = `${RangeNutrient}_g_min` | `${RangeNutrient}_g_max`

type DraftKey = ScalarKey | RangeKey

type ValorRefGrupoId = 'infantil' | 'fund1' | 'fund2' | 'eja'

type ValorRefGrupo = {
  id: ValorRefGrupoId
  label: string
  modalidades: readonly ModalidadeEnsino[]
  leitura: ModalidadeEnsino
  espelhaInfantil: boolean
}

/** Seletor exclusivo da aba Valores de referência (não altera cardápio/pedidos). */
const VALORES_REF_GRUPOS: readonly ValorRefGrupo[] = [
  {
    id: 'infantil',
    label: 'Infantil',
    modalidades: ['pre'],
    leitura: 'pre',
    espelhaInfantil: false,
  },
  {
    id: 'fund1',
    label: 'Fundamental Menor',
    modalidades: ['fund1'],
    leitura: 'fund1',
    espelhaInfantil: false,
  },
  {
    id: 'fund2',
    label: 'Fundamental Maior',
    modalidades: ['fund2'],
    leitura: 'fund2',
    espelhaInfantil: false,
  },
  {
    id: 'eja',
    label: 'EJA',
    modalidades: ['eja'],
    leitura: 'eja',
    espelhaInfantil: false,
  },
]

const SCALAR_FIELDS: { key: ScalarKey; label: string }[] = [
  { key: 'energia_kcal', label: 'Energia kcal' },
  { key: 'energia_kj', label: 'Energia kJ' },
  { key: 'vitamina_a_mcg', label: 'Vitamina A (mcg)' },
  { key: 'vitamina_c_g', label: 'Vitamina C (g)' },
  { key: 'calcio_mg', label: 'Cálcio (mg)' },
  { key: 'ferro_mg', label: 'Ferro (mg)' },
]

const RANGE_FIELDS: { nutrient: RangeNutrient; label: string }[] = [
  { nutrient: 'carboidratos', label: 'Carboidratos (g)' },
  { nutrient: 'proteinas', label: 'Proteínas (g)' },
  { nutrient: 'lipideos', label: 'Lipídeos (g)' },
]

type RowDraft = Record<DraftKey, string>

function emptyDraft(): RowDraft {
  return {
    energia_kcal: '0',
    energia_kj: '0',
    carboidratos_g_min: '0',
    carboidratos_g_max: '0',
    proteinas_g_min: '0',
    proteinas_g_max: '0',
    lipideos_g_min: '0',
    lipideos_g_max: '0',
    vitamina_a_mcg: '0',
    vitamina_c_g: '0',
    calcio_mg: '0',
    ferro_mg: '0',
  }
}

function draftFromRow(row: ValorReferenciaRefeicao | undefined): RowDraft {
  if (!row) return emptyDraft()
  return {
    energia_kcal: String(row.energia_kcal ?? 0),
    energia_kj: String(row.energia_kj ?? 0),
    carboidratos_g_min: String(row.carboidratos_g_min ?? 0),
    carboidratos_g_max: String(row.carboidratos_g_max ?? 0),
    proteinas_g_min: String(row.proteinas_g_min ?? 0),
    proteinas_g_max: String(row.proteinas_g_max ?? 0),
    lipideos_g_min: String(row.lipideos_g_min ?? 0),
    lipideos_g_max: String(row.lipideos_g_max ?? 0),
    vitamina_a_mcg: String(row.vitamina_a_mcg ?? 0),
    vitamina_c_g: String(row.vitamina_c_g ?? 0),
    calcio_mg: String(row.calcio_mg ?? 0),
    ferro_mg: String(row.ferro_mg ?? 0),
  }
}

function num(v: string) {
  const n = Number(String(v).replace(',', '.'))
  return Number.isFinite(n) ? n : 0
}

function validateDraft(d: RowDraft, rowLabel: string): string | null {
  for (const { nutrient, label } of RANGE_FIELDS) {
    const min = num(d[`${nutrient}_g_min`])
    const max = num(d[`${nutrient}_g_max`])
    if (min < 0 || max < 0) {
      return `${label} em ${rowLabel}: valores não podem ser negativos.`
    }
    if (max < min) {
      return `${label} em ${rowLabel}: o máximo deve ser maior ou igual ao mínimo.`
    }
  }
  return null
}

function draftToNutrients(d: RowDraft) {
  return {
    energia_kcal: num(d.energia_kcal),
    energia_kj: num(d.energia_kj),
    carboidratos_g_min: num(d.carboidratos_g_min),
    carboidratos_g_max: num(d.carboidratos_g_max),
    proteinas_g_min: num(d.proteinas_g_min),
    proteinas_g_max: num(d.proteinas_g_max),
    lipideos_g_min: num(d.lipideos_g_min),
    lipideos_g_max: num(d.lipideos_g_max),
    vitamina_a_mcg: num(d.vitamina_a_mcg),
    vitamina_c_g: num(d.vitamina_c_g),
    calcio_mg: num(d.calcio_mg),
    ferro_mg: num(d.ferro_mg),
  }
}

type Props = {
  onError: (msg: string | null) => void
  onInfo: (msg: string | null) => void
}

export function ValoresReferenciaSection({ onError, onInfo }: Props) {
  const { canWrite } = useAuth()
  const writable = canWrite('valores_referencia')
  const [grupoId, setGrupoId] = useState<ValorRefGrupoId>('infantil')
  const [dbRows, setDbRows] = useState<ValorReferenciaRefeicao[]>([])
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({})
  const [baseline, setBaseline] = useState<Record<string, RowDraft>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const grupo = useMemo(
    () => VALORES_REF_GRUPOS.find((g) => g.id === grupoId) || VALORES_REF_GRUPOS[0],
    [grupoId],
  )

  const dirty = useMemo(() => JSON.stringify(drafts) !== JSON.stringify(baseline), [drafts, baseline])

  const load = useCallback(async () => {
    setLoading(true)
    onError(null)
    const { data, error } = await supabase.from('valores_referencia_refeicao').select('*')
    if (error) onError(error.message)
    else setDbRows((data as ValorReferenciaRefeicao[]) || [])
    setLoading(false)
  }, [onError])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const next: Record<string, RowDraft> = {}
    for (const t of TURNOS_REFERENCIA) {
      const row = dbRows.find((r) => r.modalidade === grupo.leitura && r.turno === t.value)
      next[t.value] = draftFromRow(row)
    }
    setDrafts(next)
    setBaseline(next)
  }, [dbRows, grupo])

  function updateDraft(turno: TurnoReferencia, key: DraftKey, value: string) {
    setDrafts((prev) => ({
      ...prev,
      [turno]: { ...(prev[turno] || emptyDraft()), [key]: value },
    }))
  }

  async function save() {
    if (!writable) return
    onError(null)

    for (const t of TURNOS_REFERENCIA) {
      const err = validateDraft(drafts[t.value] || emptyDraft(), t.label)
      if (err) {
        onError(err)
        return
      }
    }

    setSaving(true)
    const now = new Date().toISOString()
    const payloads = grupo.modalidades.flatMap((modalidade) =>
      TURNOS_REFERENCIA.map((t) => ({
        modalidade,
        turno: t.value,
        ...draftToNutrients(drafts[t.value] || emptyDraft()),
        atualizado_em: now,
      })),
    )
    const { error } = await supabase.from('valores_referencia_refeicao').upsert(payloads, {
      onConflict: 'modalidade,turno',
    })
    setSaving(false)
    if (error) {
      onError(error.message)
      return
    }
    onInfo(`Valores de referência salvos (${grupo.label}).`)
    await load()
  }

  if (loading) return <div className="loading">Carregando valores de referência…</div>

  const energyFields = SCALAR_FIELDS.filter((f) => f.key === 'energia_kcal' || f.key === 'energia_kj')
  const microFields = SCALAR_FIELDS.filter((f) => f.key !== 'energia_kcal' && f.key !== 'energia_kj')

  return (
    <div>
      <div className="filters" style={{ marginBottom: 12 }}>
        <div className="field">
          <label>Modalidade</label>
          <select
            value={grupoId}
            onChange={(e) => setGrupoId(e.target.value as ValorRefGrupoId)}
            disabled={saving}
          >
            {VALORES_REF_GRUPOS.map((g) => (
              <option key={g.id} value={g.id}>
                {g.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="kpi-sub" style={{ marginBottom: 12 }}>
        Meta do <strong>pacote do turno</strong> (não por refeição isolada). A ficha técnica continua por
        refeição; a conferência usa o somatório das refeições do pacote. Carboidratos, proteínas e
        lipídeos usam faixa (mín–máx).
      </p>
      <div className="table-wrap">
        <table className="data-table data-table-valores-ref">
          <thead>
            <tr>
              <th>Turno / pacote</th>
              {energyFields.map((f) => (
                <th key={f.key} className="col-num">
                  {f.label}
                </th>
              ))}
              {RANGE_FIELDS.map((f) => (
                <th key={f.nutrient} className="col-num col-range">
                  {f.label}
                  <span className="valores-ref-th-hint">mín – máx</span>
                </th>
              ))}
              {microFields.map((f) => (
                <th key={f.key} className="col-num">
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TURNOS_REFERENCIA.map((t) => {
              const d = drafts[t.value] || emptyDraft()
              return (
                <tr key={t.value}>
                  <td>
                    <div>{t.label}</div>
                    <div className="kpi-sub" style={{ margin: 0, fontSize: '0.85em' }}>
                      {t.pacote}
                    </div>
                  </td>
                  {energyFields.map((f) => (
                    <td key={f.key} className="col-num">
                      <input
                        type="number"
                        step="any"
                        min={0}
                        value={d[f.key]}
                        disabled={!writable || saving}
                        onChange={(e) => updateDraft(t.value, f.key, e.target.value)}
                      />
                    </td>
                  ))}
                  {RANGE_FIELDS.map((f) => {
                    const minKey = `${f.nutrient}_g_min` as RangeKey
                    const maxKey = `${f.nutrient}_g_max` as RangeKey
                    return (
                      <td key={f.nutrient} className="col-num col-range">
                        <div className="valores-ref-range">
                          <input
                            type="number"
                            step="any"
                            min={0}
                            aria-label={`${t.label} ${f.label} mínimo`}
                            value={d[minKey]}
                            disabled={!writable || saving}
                            onChange={(e) => updateDraft(t.value, minKey, e.target.value)}
                          />
                          <span aria-hidden>–</span>
                          <input
                            type="number"
                            step="any"
                            min={0}
                            aria-label={`${t.label} ${f.label} máximo`}
                            value={d[maxKey]}
                            disabled={!writable || saving}
                            onChange={(e) => updateDraft(t.value, maxKey, e.target.value)}
                          />
                        </div>
                      </td>
                    )
                  })}
                  {microFields.map((f) => (
                    <td key={f.key} className="col-num">
                      <input
                        type="number"
                        step="any"
                        min={0}
                        value={d[f.key]}
                        disabled={!writable || saving}
                        onChange={(e) => updateDraft(t.value, f.key, e.target.value)}
                      />
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {writable && dirty && (
        <div className="page-actions" style={{ marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar alterações'}
          </button>
          <span className="kpi-sub" style={{ margin: 0 }}>
            Há alterações não salvas em {grupo.label}.
          </span>
        </div>
      )}
    </div>
  )
}
